import { QueryClient } from "@tanstack/react-query";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

import { ApiError, OfflineError } from "./api";

const CACHE_KEY = "wakaskul-query-cache";

/**
 * Persisting the query cache to IndexedDB is most of what "graceful
 * degradation" means in practice. A teacher opening the app in a classroom
 * with no signal sees their class list and yesterday's register immediately,
 * marked as stale, instead of a spinner and an error.
 *
 * IndexedDB rather than localStorage: the cache will hold entire class lists,
 * and localStorage's ~5 MB ceiling is both too small and synchronous.
 */
export const idbPersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    await set(CACHE_KEY, client);
  },
  restoreClient: async () => get<PersistedClient>(CACHE_KEY),
  removeClient: async () => {
    await del(CACHE_KEY);
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Long enough that switching between screens does not refetch, short
      // enough that a bursar is not looking at an hour-old fee balance.
      staleTime: 60_000,
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // A refused request will stay refused. Retrying a 403 just wastes a
        // parent's data bundle.
        if (error instanceof ApiError && error.status < 500) return false;
        if (error instanceof OfflineError) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // Writes are never retried automatically. A duplicated fee payment is
      // far worse than a failed one the bursar can see and repeat.
      retry: false,
    },
  },
});

/**
 * Anything user-specific must not survive a logout — the next person to use a
 * shared staffroom computer must not find the previous user's cached data.
 */
export async function clearPersistedCache(): Promise<void> {
  queryClient.clear();
  await idbPersister.removeClient();
}
